/* eslint-disable react-refresh/only-export-components -- co-locating Provider + hooks for the reference-meta-store context is intentional and matches the plan spec */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';

import type { RefMeta } from './references-context';
import { ReferenceMetaStore } from './reference-meta-store';

const ReferenceMetaStoreContext = createContext<ReferenceMetaStore | null>(
  null,
);

/**
 * Keeps a `ReferenceMetaStore` in sync with `value` (typically the same
 * `Map<string, RefMeta>` passed to `ReferencesProvider`) and exposes it to
 * `useReferenceMeta`/`useReferenceMetas`. Coexists with `ReferencesProvider`
 * — consumers migrate to the scoped hooks incrementally; nothing reading
 * `useReferenceMap()` needs to change.
 */
export function ReferenceMetaStoreProvider({
  value,
  children,
}: {
  value: Map<string, RefMeta>;
  children: ReactNode;
}) {
  // Lazily create exactly one store instance for this provider's lifetime.
  // The initializer runs once, before any children (and thus subscribers)
  // exist, so seeding it here can't trigger a cross-component render warning.
  const [store] = useState(() => {
    const initial = new ReferenceMetaStore();
    initial.setAll(value);
    return initial;
  });

  // Subsequent `value` updates (e.g. the references query refetched) must
  // notify subscribers from an effect — i.e. AFTER this render commits —
  // never synchronously during render.
  useEffect(() => {
    store.setAll(value);
  }, [store, value]);

  return (
    <ReferenceMetaStoreContext.Provider value={store}>
      {children}
    </ReferenceMetaStoreContext.Provider>
  );
}

function useReferenceMetaStore(): ReferenceMetaStore {
  const store = useContext(ReferenceMetaStoreContext);
  if (!store) {
    throw new Error(
      'useReferenceMeta/useReferenceMetas must be used within a ReferenceMetaStoreProvider',
    );
  }
  return store;
}

/** Subscribes only to the single `tokenId`'s row — re-renders only when it changes. */
export function useReferenceMeta(tokenId: string): RefMeta | undefined {
  const store = useReferenceMetaStore();
  const subscribe = useCallback(
    (listener: () => void) => store.subscribe(tokenId, listener),
    [store, tokenId],
  );
  const getSnapshot = useCallback(
    () => store.getSnapshot(tokenId),
    [store, tokenId],
  );
  return useSyncExternalStore(subscribe, getSnapshot);
}

/**
 * Subscribes only to the given `tokenIds` — re-renders only when at least
 * one of THEIR rows changes; a bump to any other token id is invisible to
 * this hook. Pass a referentially-stable array (e.g. `useMemo`'d from a
 * cluster's own sibling text) to avoid resubscribing every render.
 *
 * Returns a `Map<string, RefMeta>` (only the ids that currently resolve) so
 * existing call sites expecting the shape of `useReferenceMap()`'s value
 * (e.g. `renderTextToHtml(text, refMap)`) work unchanged.
 */
export function useReferenceMetas(
  tokenIds: readonly string[],
): Map<string, RefMeta> {
  const store = useReferenceMetaStore();
  const cacheRef = useRef<{
    tokenIds: readonly string[];
    map: Map<string, RefMeta>;
  } | null>(null);

  const subscribe = useCallback(
    (listener: () => void) => {
      const unsubscribes = tokenIds.map(tokenId =>
        store.subscribe(tokenId, listener),
      );
      return () => {
        for (const unsubscribe of unsubscribes) unsubscribe();
      };
    },
    [store, tokenIds],
  );

  const getSnapshot = useCallback(() => {
    const cached = cacheRef.current;
    const sameIds =
      !!cached &&
      cached.tokenIds.length === tokenIds.length &&
      cached.tokenIds.every((id, i) => id === tokenIds[i]);
    if (sameIds) {
      const unchanged = tokenIds.every(
        id => cached.map.get(id) === store.getSnapshot(id),
      );
      if (unchanged) return cached.map;
    }
    const next = new Map<string, RefMeta>();
    for (const tokenId of tokenIds) {
      const meta = store.getSnapshot(tokenId);
      if (meta) next.set(tokenId, meta);
    }
    cacheRef.current = { tokenIds, map: next };
    return next;
  }, [store, tokenIds]);

  return useSyncExternalStore(subscribe, getSnapshot);
}
